import { useMemo } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import storefrontCopy from '@/i18n/ns/storefront';
import { ErrorState } from '../primitives';
import { useStorefrontApi, type StoreDesign } from './api';
import { useRemote } from './use-storefront';
import { designPreviewUrl, encodeDesign } from './widget-tab';

const GROUPS = new Set([
  'colors',
  'type',
  'shape',
  'launcher',
  'panel',
  'products',
  'cart',
  'copy',
  'logo',
]);

type Fields = Record<string, unknown>;
const asFields = (value: unknown): Fields =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Fields) : {};

// A group's fields merge one by one: null removes a field, and a group left empty goes.
function mergeGroup(current: unknown, changes: Fields): Fields | null {
  const group = { ...asFields(current) };
  for (const [field, value] of Object.entries(changes)) {
    if (value === null) delete group[field];
    else group[field] = value;
  }
  return Object.keys(group).length ? group : null;
}

// Each language merges like a group; a language set to null goes, and so do the words when none is left.
function mergeTranslations(current: unknown, changes: unknown): Fields | null {
  const next = { ...asFields(current) };
  for (const [code, words] of Object.entries(asFields(changes))) {
    const merged = words === null ? null : mergeGroup(next[code], asFields(words));
    if (merged === null) delete next[code];
    else next[code] = merged;
  }
  return Object.keys(next).length ? next : null;
}

/** The merge the server applies (src/storefront/design.mjs), to show a proposal before it is approved. */
export function mergeDesignPreview(current: StoreDesign, changes: Fields): StoreDesign {
  const next: Fields = structuredClone(current ?? {});
  for (const [key, value] of Object.entries(changes ?? {})) {
    const merged =
      value === null
        ? null
        : key === 'translations'
          ? mergeTranslations(next[key], value)
          : GROUPS.has(key) && typeof value === 'object' && !Array.isArray(value)
            ? mergeGroup(next[key], value as Fields)
            : value;
    if (merged === null) delete next[key];
    else next[key] = merged;
  }
  return next as StoreDesign;
}

/**
 * In a design change's approval: the widget as it will look, so the person approves what they see.
 * A store with a version shows its real preview (its own greeting, logo and products) in the
 * proposed design; before that, the demo widget with example products.
 */
export function StorefrontDesignPreview({
  storeId,
  changes,
}: {
  storeId: string;
  changes: Record<string, unknown>;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const api = useStorefrontApi();
  const remote = useRemote(() => api.get(storeId).then((answer) => answer.store), storeId);
  const store = remote.data;
  const version = store?.draft?.version ?? store?.live?.version ?? 0;
  const preview = useRemote(version ? () => api.preview(storeId) : null, `${storeId}:${version}`);
  const design = useMemo(
    () => (store ? mergeDesignPreview(store.design ?? {}, changes) : null),
    [store, changes],
  );
  const src =
    !store || !design
      ? null
      : version
        ? preview.data
          ? `${preview.data.frameUrl}&design=${encodeDesign(design)}`
          : null
        : designPreviewUrl({ ...store, design }, 'computer');
  if (remote.error || preview.error) {
    return (
      <ErrorState
        message={copy.widgetFailed}
        onRetry={() => {
          remote.refresh();
          preview.refresh();
        }}
      />
    );
  }
  if (!src)
    return (
      <div className="sf-design-proposal-frame sf-design-proposal-loading" aria-hidden="true" />
    );
  return (
    <figure className="sf-design-proposal" data-testid="sf-design-proposal">
      <iframe
        className="sf-design-proposal-frame"
        data-testid="sf-design-proposal-frame"
        title={copy.proposedDesign}
        src={src}
        // Product links open in a new tab; the preview can never navigate Chat itself.
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      />
      <figcaption>{version ? copy.proposedDesignLive : copy.proposedDesignNote}</figcaption>
    </figure>
  );
}
